/**
 * Helper pour les calculs d'efficacité, de probabilités 
 * basés sur les règles du Keno FDJ 2025 (version spécifique mentionnée dans la demande).
 *
 * Règles de base utilisées ici :
 * - Total de numéros : 56
 * - Numéros tirés : 16
 * - Grilles jouables : de 4 à 10 numéros
 */

// Constantes fondamentales du jeu
export const TOTAL_NUMBERS = 56;
export const DRAWN_NUMBERS = 16;

// Pricing (synced avec Stripe dashboard)
export const PRICING = {
  // « 59 € payés à l'année, soit 4,92 € par mois » : l'annuel s'ancre sur le mensuel, jamais sur une mise.
  annual: { amount: 59, display: "59,00 €", perMonth: "4,92 €" },
  renewal: { amount: 39, display: "39,00 €" },
  monthly: { amount: 5.99, display: "5,99 €", perMonth: "5,99 €/mois" },
  // Offre du 06/10/2026 : 6 mois pour 19 €, réservée aux comptes sans abonnement en cours.
  // Prix de comparaison (décision du 02/11/2026) : 6 mois au mensuel, 6 × 5,99 € = 35,94 €, un prix
  // réellement pratiqué ; l'ancien prix barré de 34,99 € ne l'avait jamais été.
  semester: { amount: 19, display: "19,00 €", perMonth: "3,17 €/mois", strike: 35.94 },
} as const;

/** Remise de l'offre 6 mois face à 6 mois au mensuel, arrondie à l'unité inférieure (jamais surestimée). */
export const REMISE_SIX_MOIS = Math.floor((1 - PRICING.semester.amount / PRICING.semester.strike) * 100);

// Structure de données pour les gains par taille de grille
// La clé est le nombre de correspondances (matches), la valeur est le multiplicateur de gain pour 1€
type MultiplierMap = Record<number, number>;

// Définition des gains officiels pour chaque taille de grille (4 à 10 numéros)
// Note: Les clés sont le nombre de bons numéros trouvés.
export const KENO_2025_GAINS: Record<number, MultiplierMap> = {
  4: { 3: 3, 4: 70 },
  5: { 3: 2, 4: 10, 5: 80 },
  6: { 4: 3, 5: 30, 6: 900 },
  7: { 4: 2, 5: 5, 6: 90, 7: 3000 },
  8: { 0: 2, 5: 5, 6: 30, 7: 100, 8: 8000 },
  9: { 0: 2, 4: 1, 5: 2, 6: 8, 7: 25, 8: 100, 9: 30000 },
  10: { 0: 2, 5: 2, 6: 5, 7: 15, 8: 150, 9: 2000, 10: 200000 },
};

/**
 * Returns the gain multiplier (€ per €1 stake) for a (gridSize, matchCount) pair, or null if not a winning combination.
 * Note: on grids 8/9/10, matchCount===0 is a winning "boule blanche" (2x stake).
 */
export function computeGain(gridSize: number, matchCount: number): number | null {
  const gains = KENO_2025_GAINS[gridSize];
  if (!gains) return null;
  return gains[matchCount] ?? null;
}

export function isLuckyZero(gridSize: number, matchCount: number): boolean {
  return matchCount === 0 && (computeGain(gridSize, 0) ?? 0) > 0;
}

// Interface pour les détails d'un rang de gain
export interface GainTier {
  matches: number;
  multiplier: number;
  probability: number;
  probabilityOneIn: number; // "1 chance sur X"
}

// Interface principale pour l'efficacité d'une grille
export interface GridEfficiency {
  gridSize: number;
  gains: GainTier[];
  totalProbabilityToWin: number; // Probabilité de gagner au moins un lot
  totalWinOneIn: number; // "1 chance sur X" de gagner quelque chose
}

/**
 * Calcule la factorielle d'un nombre n.
 * Utilise BigInt pour éviter les dépassements de capacité avec les grands nombres.
 */
function factorial(n: number): bigint {
  if (n < 0) return BigInt(0); // Mathématiquement indéfini pour n < 0, retour 0 par sécurité
  if (n === 0) return BigInt(1);
  let result = BigInt(1);
  for (let i = 2; i <= n; i++) {
    result *= BigInt(i);
  }
  return result;
}

/**
 * Calcule le nombre de combinaisons de k éléments parmi n (C(n, k)).
 * Formule : n! / (k! * (n - k)!)
 */
export function combinations(n: number, k: number): bigint {
  if (k < 0 || k > n) return BigInt(0);
  if (k === 0 || k === n) return BigInt(1);
  if (k > n / 2) k = n - k; // Optimisation symétrique

  // Calcul direct pour éviter les factorielles géantes intermédiaires si possible,
  // mais BigInt gère bien les factorielles jusqu'à 70! sans problème.
  // Pour la clarté et la robustesse, on utilise la formule factorielle avec BigInt.
  return factorial(n) / (factorial(k) * factorial(n - k));
}

/**
 * Calcule la probabilité d'avoir exactement 'matches' bons numéros
 * pour une grille de taille 'gridSize' sur un tirage de 'DRAWN_NUMBERS' numéros
 * parmi 'TOTAL_NUMBERS'.
 *
 * Formule hypergéométrique :
 * P(X=k) = [ C(Bons, k) * C(Mauvais, TailleGrille - k) ] / C(Total, TailleGrille)
 *
 * Où :
 * - Bons = DRAWN_NUMBERS (16)
 * - Mauvais = TOTAL_NUMBERS - DRAWN_NUMBERS (56 - 16 = 40)
 * - Total = TOTAL_NUMBERS (56)
 */
export function calculateProbability(
  gridSize: number,
  matches: number
): number {
  const winningNumbers = DRAWN_NUMBERS; // 16
  const losingNumbers = TOTAL_NUMBERS - DRAWN_NUMBERS; // 40

  // Si on demande plus de matches que possible (ex: 5 matches sur une grille de 4), proba 0
  if (matches > gridSize) return 0;
  // Si on demande plus de matches que de numéros tirés, proba 0
  if (matches > winningNumbers) return 0;
  // Si le reste de la grille (les mauvais numéros) dépasse le nombre de mauvais numéros disponibles
  if (gridSize - matches > losingNumbers) return 0;

  const waysToChooseWinning = combinations(winningNumbers, matches);
  const waysToChooseLosing = combinations(losingNumbers, gridSize - matches);
  const totalWaysToChooseGrid = combinations(TOTAL_NUMBERS, gridSize);

  // Conversion en Number pour le résultat final (précision suffisante pour l'affichage)
  // Attention : la division de BigInt tronque, donc on convertit avant ou on utilise une échelle.
  // Ici on convertit en Number car les probabilités sont < 1.
  // Pour garder de la précision, on peut faire : Number(num) / Number(den)
  const numerator = Number(waysToChooseWinning * waysToChooseLosing);
  const denominator = Number(totalWaysToChooseGrid);

  return numerator / denominator;
}

/**
 * Génère les données complètes d'efficacité pour une taille de grille spécifique.
 */
export function getEfficiencyForGridSize(gridSize: number): GridEfficiency {
  const multipliers = KENO_2025_GAINS[gridSize];
  if (!multipliers) {
    throw new Error(`Taille de grille non supportée : ${gridSize}`);
  }

  const gains: GainTier[] = [];
  let totalProbabilityToWin = 0;

  // Calculer les stats pour chaque rang gagnant
  // On trie les clés pour avoir un ordre logique (souvent croissant de matches, mais 0 est spécial)
  const sortedMatches = Object.keys(multipliers)
    .map(Number)
    .sort((a, b) => a - b);

  for (const matches of sortedMatches) {
    const multiplier = multipliers[matches];
    const probability = calculateProbability(gridSize, matches);
    
    // "1 chance sur X" = 1 / probabilité
    const probabilityOneIn = probability > 0 ? 1 / probability : 0;

    gains.push({
      matches,
      multiplier,
      probability,
      probabilityOneIn,
    });

    totalProbabilityToWin += probability;
  }

  const totalWinOneIn =
    totalProbabilityToWin > 0 ? 1 / totalProbabilityToWin : 0;

  return {
    gridSize,
    gains,
    totalProbabilityToWin,
    totalWinOneIn,
  };
}

/**
 * Retour théorique d'une grille sans multiplicateur, en pourcentage de la mise
 * (somme probabilité × gain de base, mise 1 €). Miroir de App\\Helper\\KenoOdds::returnRate.
 * Ex. 4 numéros : 52,98 ; 10 numéros : 47,24.
 */
export function getTheoreticalReturnPct(gridSize: number): number {
  return getEfficiencyForGridSize(gridSize).gains.reduce(
    (sum, tier) => sum + tier.probability * tier.multiplier,
    0,
  ) * 100;
}

/**
 * Retourne un tableau complet des données d'efficacité pour toutes les grilles supportées (4 à 10).
 */
export function getGridEfficiencyData(): GridEfficiency[] {
  const supportedSizes = [4, 5, 6, 7, 8, 9, 10];
  return supportedSizes.map((size) => getEfficiencyForGridSize(size));
}

/**
 * Formate un nombre "1 chance sur X" pour l'affichage.
 * Ex: 12345.67 -> "12 346"
 */
export function formatOneIn(value: number): string {
  if (value >= 100) {
    return Math.round(value).toLocaleString("fr-FR");
  }
  if (value >= 10) {
    return value.toFixed(1).replace(".", ",");
  }
  return value.toFixed(2).replace(".", ",");
}

/**
 * Formate un pourcentage avec 2 décimales.
 */
export function formatPercent(value: number): string {
  return `${value.toFixed(2).replace(".", ",")}%`;
}